import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../api';
import CreatorNavbar from '../components/dashboard/CreatorNavbar';
import CreatorPageHeader from '../components/dashboard/CreatorPageHeader';

const CreatorProjects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeProjects, setActiveProjects] = useState([]);
  const [completedProjects, setCompletedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'creator') return;

    const fetchProjects = async () => {
      try {
        const response = await api.get(`/creators/${user.userId}/projects`);
        const projects = response.data;
        
        // Separate active and completed projects
        setActiveProjects(projects.filter(p => p.status !== 'completed'));
        setCompletedProjects(projects.filter(p => p.status === 'completed'));
      } catch (err) {
        // Error handled silently
        // Fallback: try to get projects from dashboard endpoint
        try {
          const dashboardResponse = await api.get(`/creators/${user.userId}/dashboard`);
          setActiveProjects(dashboardResponse.data.activeProjects || []);
        } catch (dashboardErr) {
          // Dashboard fetch also failed
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [user]);

  const handleSubmitContent = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post(`/projects/${selectedProject.id}/submit`, formData);
      alert('Content submitted successfully!');
      setShowSubmitModal(false);
      // Refresh projects after submission
      window.location.reload();
    } catch (err) {
      alert('Submission failed. Please try again.');
    }
  };

  const handleCompleteProject = async (bidId) => {
    if (!bidId) {
      alert('Project information not found');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to mark this project as completed?');
    if (!confirmed) return;

    try {
      await api.post(`/creators/${user.userId}/bids/${bidId}/complete`);
      alert('Project marked as completed successfully!');

      // Refresh projects
      window.location.reload();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to complete project';
      alert(errorMsg);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'in_progress':
      case 'in progress':
        return 'text-blue-600 bg-blue-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'cancelled':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (!user) return <p className="p-8 text-center">Please log in to view your projects.</p>;
  if (user.role !== 'creator') return <p className="p-8 text-center">Access denied. Only Creators can view this page.</p>;
  if (loading) return <p className="p-8 text-center">Loading your projects...</p>;

  return (
    <>
      <Helmet>
        <title>My Projects - CollabOrb Creator</title>
        <meta name="description" content="Manage your active and completed projects on CollabOrb creator platform" />
      </Helmet>
      
      <CreatorNavbar />
      
      <div className="pt-16 sm:pt-20">
        <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <CreatorPageHeader
          kicker="Project workspace"
          title="My Projects"
          description="Track active deliveries, deadlines, and completed brand collaborations in one place."
          actions={
            <button
              onClick={() => navigate('/dashboard/creator')}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors duration-200 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Back to Dashboard
            </button>
          }
        />

        {/* Active Projects */}
        <section className="bg-white p-6 rounded-xl shadow">
          <h2 className="text-2xl font-semibold mb-6 text-blue-600">Active Projects</h2>
          {activeProjects.length > 0 ? (
            <div className="space-y-4">
              {activeProjects.map((project) => (
                <div key={project.id} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {project.brandLogoUrl && (
                        <img 
                          src={project.brandLogoUrl} 
                          alt={project.brandName} 
                          className="h-12 w-12 rounded-full object-cover flex-shrink-0" 
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">{project.brandName}</p>
                        {project.description && (
                          <p className="text-sm text-gray-700 mb-3">{project.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm">
                          {project.deadline && (
                            <span className="text-red-600 font-medium">
                              📅 Deadline: {new Date(project.deadline).toLocaleDateString()}
                            </span>
                          )}
                          {project.reward && (
                            <span className="text-green-600 font-medium">💰 Reward: ₹{project.reward?.toLocaleString('en-IN')}</span>
                          )}
                          {project.budget && !project.reward && (
                            <span className="text-green-600 font-medium">💰 Budget: ₹{project.budget?.toLocaleString('en-IN')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status || 'Active'}
                      </span>
                      <button
                        onClick={() => handleCompleteProject(project.bidId)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Mark as Completed
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="text-gray-500 text-lg">No active projects yet</p>
              <p className="text-gray-400 text-sm mt-2">Check back later for new collaboration opportunities</p>
            </div>
          )}
        </section>

        {/* Completed Projects */}
        <section className="bg-white p-6 rounded-xl shadow">
          <h2 className="text-2xl font-semibold mb-6 text-green-600">Completed Projects</h2>
          {completedProjects.length > 0 ? (
            <div className="space-y-4">
              {completedProjects.map((project) => (
                <div key={project.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {project.brandLogoUrl && (
                        <img 
                          src={project.brandLogoUrl} 
                          alt={project.brandName} 
                          className="h-12 w-12 rounded-full object-cover flex-shrink-0" 
                        />
                      )}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{project.title}</h3>
                        <p className="text-sm text-gray-600 mb-2">{project.brandName}</p>
                        {project.description && (
                          <p className="text-sm text-gray-700 mb-3">{project.description}</p>
                        )}
                        <div className="flex flex-wrap gap-4 text-sm">
                          {project.completedDate && (
                            <span className="text-green-600 font-medium">
                              ✅ Completed: {new Date(project.completedDate).toLocaleDateString()}
                            </span>
                          )}
                          {project.budget && (
                            <span className="text-green-600 font-medium">💰 Earned: ₹{project.budget?.toLocaleString('en-IN')}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600">
                      Completed
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-500">No completed projects yet</p>
            </div>
          )}
        </section>
        </div>
      </div>

      {/* Submit Content Modal */}
      {showSubmitModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md mx-4">
            <h3 className="text-xl font-bold mb-4">Submit Content for {selectedProject.title}</h3>
            <form onSubmit={handleSubmitContent} className="space-y-4">
              <div>
                <label htmlFor="contentFile" className="block text-sm font-medium text-gray-700 mb-2">Upload Content File</label>
                <input 
                  id="contentFile"
                  type="file" 
                  name="file" 
                  required 
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                />
              </div>
              <div>
                <label htmlFor="submissionNotes" className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
                <textarea
                  id="submissionNotes"
                  name="notes"
                  placeholder="Add any notes about your submission..."
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="4"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowSubmitModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors duration-200"
                >
                  Submit Content
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default CreatorProjects;
